import { exportFilename } from '../../data/export';
import type { SyncMeta, SyncProvider } from './SyncProvider';

/**
 * Backup to the Files app. On iPadOS a download from an installed PWA lands in
 * Files, from where iCloud or AirDrop can take it off the device — which is the
 * point: a backup that only exists on the iPad protects against nothing that
 * actually happens to iPads.
 *
 * Both calls must originate in a user gesture (a tap in Betreuer-Modus). That is a
 * platform constraint, not a design choice, so there is no timer-driven path here.
 */
export const fileExportProvider: SyncProvider = {
  id: 'fileExport',

  async push(blob: Blob, meta: SyncMeta): Promise<void> {
    const url = URL.createObjectURL(blob);
    try {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = exportFilename(meta.createdAt);
      anchor.rel = 'noopener';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } finally {
      // Safari needs the URL to survive the click; a task boundary is enough.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    }
  },

  pull(): Promise<{ blob: Blob; createdAt: string } | null> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'application/json,.json';
      input.style.display = 'none';

      const finish = (result: { blob: Blob; createdAt: string } | null): void => {
        input.remove();
        resolve(result);
      };

      input.addEventListener('change', () => {
        const file = input.files?.[0];
        if (!file) {
          finish(null);
          return;
        }
        finish({ blob: file, createdAt: new Date(file.lastModified).toISOString() });
      });
      // Safari fires `cancel` when the picker is dismissed; without this the promise
      // would never settle and the caregiver would be looking at a dead button.
      input.addEventListener('cancel', () => finish(null));

      document.body.append(input);
      input.click();
    });
  },

  /**
   * Always empty, and that is the honest answer rather than a stub.
   *
   * The Files app cannot be enumerated from a web app — there is no API, and there could not be one
   * without handing every site a directory listing. What replaces a version list here is the file picker
   * itself: the caregiver sees `sags-backup-2026-07-29.json` next to every other date and chooses. So the
   * capability exists, it is just the platform's rather than ours.
   */
  async versions(): Promise<[]> {
    return [];
  },

  isConfigured(): boolean {
    return true;
  },
};
